import type { ComponentPropsWithoutRef } from "react"

import { cn } from "@/lib/utils"

import { KLERI_PATH, KLERI_TONES, KLERI_VIEWBOX, type KleriTone } from "./kleri-logo-utils"

type KleriLogoProps = Omit<ComponentPropsWithoutRef<"svg">, "fill" | "title" | "viewBox"> & {
  className?: string
  /** One of the three approved logo colours; `auto` (default) inherits `currentColor`. */
  tone?: KleriTone
  /** Accessible name. Omit on decorative marks — the svg is then hidden from AT. */
  title?: string
  /** Brand "focus" motion: enters blurred, sharpens into place. */
  entrance?: "none" | "focus"
  entranceDelayMs?: number
}

/**
 * The Kleri wordmark (lockup D). Keep it at or above `KLERI_MIN_WIDTH_PX` and
 * leave `KLERI_CLEARSPACE_RATIO` of the K height clear on every side.
 */
export function KleriLogo({
  className,
  entrance = "none",
  entranceDelayMs,
  style,
  title,
  tone = "auto",
  ...rest
}: KleriLogoProps) {
  const toneColor = KLERI_TONES[tone]

  return (
    <svg
      {...rest}
      aria-hidden={title ? undefined : true}
      className={cn("logo", entrance === "focus" && "brand-focus-entrance", className)}
      fill="currentColor"
      role={title ? "img" : "presentation"}
      viewBox={KLERI_VIEWBOX}
      xmlns="http://www.w3.org/2000/svg"
      style={{
        ...(toneColor ? { color: toneColor } : undefined),
        ...(entrance === "focus" && entranceDelayMs
          ? { animationDelay: `${entranceDelayMs}ms` }
          : undefined),
        ...style,
      }}
    >
      {title ? <title>{title}</title> : null}
      <path d={KLERI_PATH} />
    </svg>
  )
}
